// src/hooks/useSearch.ts
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RequestType } from '../utils/pipelineSteps';
import type { ChatLocationState } from '../types/chat';

interface UseSearchReturn {
  handleSearch: (query: string) => void;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

interface LoadingState {
  isLoading: boolean;
  requestType?: RequestType;
  query?: string;
}

// Global loading state for the loading overlay - counter-based to support multiple operations
export const searchLoadingState = {
  _loadingCount: 0,
  _currentRequestType: undefined as RequestType | undefined,
  _currentQuery: undefined as string | undefined,
  listeners: new Set<(state: LoadingState) => void>(),
  
  get isLoading() {
    return this._loadingCount > 0;
  },
  
  get requestType() {
    return this._currentRequestType;
  },
  
  get query() {
    return this._currentQuery;
  },
  
  incrementLoading(requestType?: RequestType, query?: string) {
    this._loadingCount++;
    if (requestType) {
      this._currentRequestType = requestType;
    }
    if (query) {
      this._currentQuery = query;
    }
    this.notifyListeners();
  },
  
  decrementLoading() {
    if (this._loadingCount > 0) {
      this._loadingCount--;
    }
    if (this._loadingCount === 0) {
      this._currentRequestType = undefined;
      this._currentQuery = undefined;
    }
    this.notifyListeners();
  },
  
  setLoading(loading: boolean, requestType?: RequestType, query?: string) {
    if (loading) {
      this.incrementLoading(requestType, query);
    } else {
      this.decrementLoading();
    }
  },
  
  updateRequestType(requestType: RequestType, query?: string) {
    this._currentRequestType = requestType;
    if (query) {
      this._currentQuery = query;
    }
    this.notifyListeners();
  },
  
  notifyListeners() {
    const state: LoadingState = {
      isLoading: this.isLoading,
      requestType: this._currentRequestType,
      query: this._currentQuery,
    };
    this.listeners.forEach(listener => listener(state));
  },
  
  subscribe(listener: (state: LoadingState) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
};

export const useSearch = (): UseSearchReturn => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const handleSearch = (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;

    setError(null);
    const state: ChatLocationState = {
      initialMessage: trimmed,
      fromSearch: true,
    };
    navigate('/chat', { state });
  };

  const clearError = () => {
    setError(null);
  };

  return {
    handleSearch,
    isLoading: false,
    error,
    clearError,
  };
};
